import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react'
import { AppBootScreen } from '@/components/app/AppBootScreen'
import { HomeLandingPage } from '@/pages/home/HomeLandingPage'
import type { AppLocale } from '@/lib/i18n'
import { normalizeAppPath, type PropertyRegisterAccountMode } from '@/lib/navigation'

const PrivacyPolicyPage = lazy(() =>
  import('@/pages/legal/PrivacyPolicyPage').then((module) => ({ default: module.PrivacyPolicyPage })),
)
const TermsOfUsePage = lazy(() =>
  import('@/pages/legal/TermsOfUsePage').then((module) => ({ default: module.TermsOfUsePage })),
)
const AgentTermsPage = lazy(() =>
  import('@/pages/legal/AgentTermsPage').then((module) => ({ default: module.AgentTermsPage })),
)
const DeleteAccountPage = lazy(() =>
  import('@/pages/legal/DeleteAccountPage').then((module) => ({ default: module.DeleteAccountPage })),
)
const KprSimulationPage = lazy(() =>
  import('@/pages/kpr/KprSimulationPage').then((module) => ({ default: module.KprSimulationPage })),
)
const KprSyariahSimulationPage = lazy(() =>
  import('@/pages/kpr/KprSyariahSimulationPage').then((module) => ({ default: module.KprSyariahSimulationPage })),
)
const PropertyRegisterPage = lazy(() =>
  import('@/pages/property/PropertyRegisterPage').then((module) => ({ default: module.PropertyRegisterPage })),
)
const PropertyListingDetailPage = lazy(() =>
  import('@/pages/property/PropertyListingDetailPage').then((module) => ({ default: module.PropertyListingDetailPage })),
)
const PropertyListingSearchPage = lazy(() =>
  import('@/pages/property/PropertyListingSearchPage').then((module) => ({ default: module.PropertyListingSearchPage })),
)
const PropertyPartnerPage = lazy(() =>
  import('@/pages/property/PropertyPartnerPage').then((module) => ({ default: module.PropertyPartnerPage })),
)
const PropertyCompetitorPage = lazy(() =>
  import('@/pages/property/PropertyCompetitorPage').then((module) => ({ default: module.PropertyCompetitorPage })),
)
const PropertyAboutPage = lazy(() =>
  import('@/pages/property/PropertyAboutPage').then((module) => ({ default: module.PropertyAboutPage })),
)
const PropertyFaqPage = lazy(() =>
  import('@/pages/property/PropertyFaqPage').then((module) => ({ default: module.PropertyFaqPage })),
)
const PropertyArticlesPage = lazy(() =>
  import('@/pages/property/PropertyArticlesPage').then((module) => ({ default: module.PropertyArticlesPage })),
)
const PropertyLocationsPage = lazy(() =>
  import('@/pages/property/PropertyLocationsPage').then((module) => ({ default: module.PropertyLocationsPage })),
)
const PropertyUnavailablePage = lazy(() =>
  import('@/pages/property/PropertyUnavailablePage').then((module) => ({ default: module.PropertyUnavailablePage })),
)

const SOCIAL_APP_ORIGIN = 'https://social.dihaw.ai'

type AppRouterProps = {
  locale: AppLocale
  onLocaleChange: (nextLocale: AppLocale) => void
  updatePrompt: ReactNode
}

function getCurrentPathname() {
  return typeof window !== 'undefined' ? normalizeAppPath(window.location.pathname) : '/'
}

function redirectToSocialApp() {
  if (typeof window === 'undefined') return
  window.location.replace(`${SOCIAL_APP_ORIGIN}${window.location.pathname}${window.location.search}${window.location.hash}`)
}

function getListingKeyFromPath(pathname: string) {
  const match = pathname.match(/^\/(?:listing|properti)\/([^/]+)\/?$/)
  return match?.[1] || ''
}

function getArticleSlugFromPath(pathname: string) {
  if (pathname === '/artikel') return ''
  const match = pathname.match(/^\/artikel\/([^/]+)\/?$/)
  return match?.[1] || null
}

export function AppRouter({ locale, onLocaleChange, updatePrompt }: AppRouterProps) {
  const [currentPathname, setCurrentPathname] = useState(getCurrentPathname)
  const [registerModalMode, setRegisterModalMode] = useState<PropertyRegisterAccountMode | null>(null)

  useEffect(() => {
    const handlePathChange = () => setCurrentPathname(getCurrentPathname())
    window.addEventListener('popstate', handlePathChange)
    return () => window.removeEventListener('popstate', handlePathChange)
  }, [])

  useEffect(() => {
    const handleOpenPropertyRegister = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail as { accountMode?: PropertyRegisterAccountMode } : {}
      setRegisterModalMode(detail.accountMode === 'agent' ? 'agent' : 'user')
    }
    window.addEventListener('dihaw:open-property-register', handleOpenPropertyRegister)
    return () => window.removeEventListener('dihaw:open-property-register', handleOpenPropertyRegister)
  }, [])

  useEffect(() => {
    if (currentPathname.startsWith('/app')) {
      redirectToSocialApp()
    }
  }, [currentPathname])

  const legalProps = {
    locale,
    isDark: false,
    onToggleTheme: () => {},
    onLocaleChange,
    onOpenAuth: () => {},
  }

  const registerModal = registerModalMode ? (
    <Suspense fallback={null}>
      <PropertyRegisterPage
        locale={locale}
        onLocaleChange={onLocaleChange}
        mode="register"
        accountMode={registerModalMode}
        modalOnly
        onClose={() => setRegisterModalMode(null)}
      />
    </Suspense>
  ) : null

  if (currentPathname.startsWith('/app')) {
    return (
      <>
        <AppBootScreen isDark={false} />
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/privacy-policy') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PrivacyPolicyPage {...legalProps} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/terms-of-use') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <TermsOfUsePage {...legalProps} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/syarat-penggunaan-agen') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <AgentTermsPage {...legalProps} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/delete-account') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <DeleteAccountPage {...legalProps} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/kpr' || currentPathname === '/kpr/simulasi-kpr') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <KprSimulationPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/kpr/syariah') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <KprSyariahSimulationPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/faq') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyFaqPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/partner') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyPartnerPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/kompetitor') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyCompetitorPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/tentang-kami') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyAboutPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/lokasi-properti') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyLocationsPage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  const articleSlug = getArticleSlugFromPath(currentPathname)
  if (articleSlug !== null) {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyArticlesPage
            locale={locale}
            onLocaleChange={onLocaleChange}
            articleSlug={articleSlug}
          />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/pasang-property' || currentPathname === '/pasang-property/profile' || currentPathname.startsWith('/pasang-property/profile/')) {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyRegisterPage
            locale={locale}
            onLocaleChange={onLocaleChange}
            mode={currentPathname === '/pasang-property' ? 'register' : 'profile'}
          />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname === '/agent' || currentPathname === '/agent/profile' || currentPathname.startsWith('/agent/profile/')) {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyRegisterPage
            locale={locale}
            onLocaleChange={onLocaleChange}
            mode={currentPathname === '/agent' ? 'register' : 'profile'}
            accountMode="agent"
          />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  const listingKey = getListingKeyFromPath(currentPathname)
  if (listingKey) {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyListingDetailPage
            listingKey={listingKey}
            locale={locale}
            onLocaleChange={onLocaleChange}
          />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (
    currentPathname === '/jual'
    || currentPathname.startsWith('/jual/')
    || currentPathname === '/sewa'
    || currentPathname.startsWith('/sewa/')
    || currentPathname === '/properti-baru'
    || currentPathname.startsWith('/properti-baru/')
    || currentPathname === '/perumahan-baru'
    || currentPathname.startsWith('/perumahan-baru/')
  ) {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyListingSearchPage
            pathname={currentPathname}
            locale={locale}
            onLocaleChange={onLocaleChange}
          />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  if (currentPathname !== '/') {
    return (
      <>
        <Suspense fallback={<AppBootScreen isDark={false} />}>
          <PropertyUnavailablePage locale={locale} onLocaleChange={onLocaleChange} />
        </Suspense>
        {registerModal}
        {updatePrompt}
      </>
    )
  }

  return (
    <>
      <HomeLandingPage
        locale={locale}
        isDark={false}
        onToggleTheme={() => {}}
        onLocaleChange={onLocaleChange}
      />
      {registerModal}
      {updatePrompt}
    </>
  )
}
